import { CheckoutContext } from '@/context/CheckoutContext';
import { TemplateType } from '@/types/boot';
import { cn } from '@/utils/cn';
import { addCommasToNumber } from '@/utils/pricing';
import { shouldShowExtraItemRows } from '@/utils/purchasedItems';
import { useContext } from 'react';

export const PurchasedItems = ({ bare = false }: { bare?: boolean } = {}) => {
  const { purchasedItems, template, customer } = useContext(CheckoutContext);
  // SINGLE (last-payment) and PREFERRED (FTD) reveal 3.5 product rows so the
  // user always sees there is more to scroll. Each row is `h-10` (40px) with
  // `gap-2` (8px) between rows, so 3 full rows + half of the next peeking
  // = 3*40 + 3*8 + 20 = 164px. DEFAULT keeps its prior heights.
  const isCompactTemplate =
    template.type === TemplateType.SINGLE_PAYMENT_METHOD || template.type === TemplateType.PREFERRED_PAYMENT_METHOD;
  // Countries flagged for a taller window (see `shouldShowExtraItemRows`) reveal
  // 5.5 rows on portrait and 6.5 on desktop, by the same `48n + 20` row math as
  // above: 5*40 + 5*8 + 20 = 260px and 6*40 + 6*8 + 20 = 308px. Applied last so
  // it beats the compact-template cap; landscape keeps its own tighter peek.
  const showExtraItemRows = shouldShowExtraItemRows(customer);
  // Text color inherits from `<Details>` (auto-contrasted per BG presence).
  return (
    <div
      data-testid="purchased-items"
      className={cn(
        // Base caps reveal 3.5 rows on portrait (164px) and 4.5 on desktop
        // (212px) by the `48n + 20` math above. They were 140px / 192px, which
        // landed on whole-row boundaries once PR #197 shrank the row from 48px
        // to 40px (3 rows = 136 of 140, 4 rows = 184 of 192) — the leftover fell
        // inside the gap, so no half-row peeked and the list looked complete.
        // Landscape gets more air between rows (gap-3) and a shorter window so
        // ~2.5 taller rows (landscape:h-12 below) peek instead of 3.5 cramped
        // ones — the wide-but-short landscape band reads as crowded otherwise.
        'flex flex-col gap-2 max-h-[164px] md:max-h-[212px] overflow-y-auto scrollbar-hide landscape:gap-3 landscape:max-h-[150px]',
        // `bare` (the white swipe-up sheet) drops the gray items box so the list
        // reads as clean rows flush with the price breakdown (Figma).
        !bare && 'bg-[rgba(0,0,0,0.1)] rounded-md',
        isCompactTemplate && 'max-h-[164px] md:max-h-[164px]',
        showExtraItemRows && 'max-h-[260px] md:max-h-[308px]'
      )}
    >
      {purchasedItems.map((item, index) => (
        <div
          key={`${item.sku}-${item.name}-${index}`}
          className="flex flex-row gap-2 items-center justify-between h-10 p-2 landscape:h-12"
        >
          <div className="flex flex-row gap-1 items-center">
            {/* `bare` also drops the muted tile behind the product image — the
                sheet shows the raw asset on white (Figma). */}
            <div className={cn('w-8 h-8 flex items-center justify-center')}>
              <img className="w-7 h-7 object-contain" src={item.assetUrl} alt={item.name} />
            </div>
            <div className="text-sm weight-light">{item.displayName ?? item.name}</div>
          </div>

          <div className="text-sm mr-2 weight-light">
            {item.quantityDisplay ?? addCommasToNumber(BigInt(item.quantity))}
          </div>
        </div>
      ))}
    </div>
  );
};
